import { produce } from "immer";

import { hydrateConditionWithIdsMutative } from "src/components/audiences/utils";
import type { JourneyQuery } from "src/graphql";

/**
 * Visual query conditions loaded from the database don't carry the `id`s that
 * the query builder uses to locate a subcondition when applying an edit
 * (see `createSubconditionUpdater`). Without ids, editing an existing
 * condition silently no-ops. Hydrate every condition-bearing field a journey
 * node config can hold so edits land.
 *
 * Mutates `config` in place; intended to run inside an immer `produce`.
 */
export const hydrateNodeConfigConditionsMutative = (
  config: unknown,
  createId?: () => string,
): void => {
  if (!config || typeof config !== "object") {
    return;
  }

  for (const field of [
    "segment_conditions",
    "event_conditions",
    "profile_conditions",
    "visual_query_conditions",
    "filter",
  ] as const) {
    if (!(field in config)) {
      continue;
    }
    const conditions = (config as Record<string, unknown>)[field];
    if (
      conditions != null &&
      typeof conditions === "object" &&
      "conditions" in conditions
    ) {
      // The mutative hydrator is typed for Condition/VisualQueryFilter; these
      // jsonb-sourced shapes satisfy that at runtime.
      hydrateConditionWithIdsMutative(
        conditions as Parameters<typeof hydrateConditionWithIdsMutative>[0],
        false,
        createId,
      );
    }
  }
};

/** A loaded journey with ids on every node and exit criteria condition, as the canvas loads it. */
export const hydrateJourneyConditionIds = (
  journey: JourneyQuery["journeys"][number],
) =>
  produce(journey, (draft) => {
    draft.nodes.forEach((node) => {
      hydrateNodeConfigConditionsMutative(node.config);
    });
    if (draft.exit_criteria && "conditions" in draft.exit_criteria) {
      hydrateConditionWithIdsMutative(draft.exit_criteria);
    }
  });
